import _ from "lodash";
import { observable, makeObservable, action } from "mobx";
import type { LLMResultDefinition, LLMPromptTemplate } from "models/LLMs";
import { LLMClassifierImplementationConfigurationPromptOnly } from "models/LLMs/LLMClassifierImplementation";
import { deepCamelCase } from "../utils";
import {
    LLMClassifierConfigurationBuilder,
    ILLMClassifierConfigurationBuilder,
} from "./LLMClassifierConfigurationBuilder";

export class LLMClassifierStandardBuilder
    extends LLMClassifierConfigurationBuilder
    implements ILLMClassifierConfigurationBuilder
{
    @observable public llmResultDefinitions?: LLMResultDefinition[];
    @observable public llmPromptTemplate?: string;
    @observable public llmPromptTemplateOption?: LLMPromptTemplate;
    @observable public questionText?: string;
    @observable public questionDefinition?: string;

    private initialValues: {
        llmResultDefinitions?: LLMResultDefinition[];
        questionText?: string;
        questionDefinition?: string;
    };

    constructor(
        configuration?: LLMClassifierImplementationConfigurationPromptOnly,
        llmResultDefinitions?: LLMResultDefinition[],
    ) {
        super(configuration);
        makeObservable(this);

        if (configuration) {
            const camelized = deepCamelCase(configuration);
            this.llmPromptTemplate = camelized.promptTemplate;
            this.questionText = camelized.questionText;
            this.questionDefinition = camelized.questionDescription;
        }

        if (llmResultDefinitions) {
            this.llmResultDefinitions = llmResultDefinitions;
        }

        this.initialValues = {
            llmResultDefinitions: _.cloneDeep(this.llmResultDefinitions),
            questionText: this.questionText,
            questionDefinition: this.questionDefinition,
        };
    }

    @action
    public setLLMPromptTemplate(template?: string) {
        this.llmPromptTemplate = template;
    }

    @action
    public setLLMPromptTemplateOption(option?: LLMPromptTemplate) {
        this.llmPromptTemplateOption = option;
    }

    @action
    public setLLMResultDefinitions(definitions?: LLMResultDefinition[]) {
        this.llmResultDefinitions = definitions;
    }

    @action
    public setQuestionText(text?: string) {
        this.questionText = text;
    }

    @action
    public setQuestionDefinition(definition?: string) {
        this.questionDefinition = definition;
    }

    @action
    public updateCriteria(
        definition: LLMResultDefinition,
        newCriteria: string,
    ) {
        definition.criteria = newCriteria;
    }

    @action
    public addExample(definition: LLMResultDefinition) {
        definition.examples.push("");
    }

    @action
    public updateExample(
        definition: LLMResultDefinition,
        exampleIndex: number,
        newExample: string,
    ) {
        definition.examples[exampleIndex] = newExample;
    }

    @action
    public removeExample(definition: LLMResultDefinition, idx: number) {
        definition.examples.splice(idx, 1);
    }

    isTestable(): boolean {
        return (
            !!this.llmPromptTemplate &&
            this.llmPromptTemplate.length > 0 &&
            !!this.questionText &&
            this.questionText.length > 0 &&
            !!this.questionDefinition &&
            this.questionDefinition.length > 0 &&
            !!this.llmResultDefinitions &&
            this.llmResultDefinitions.length > 0 &&
            this.llmResultDefinitions.every(
                (definition) =>
                    definition.criteria && definition.criteria.length > 0,
            )
        );
    }

    isSavable(): boolean {
        return (
            !!this.llmPromptTemplate &&
            this.llmPromptTemplate.length > 0 &&
            !!this.questionText &&
            this.questionText.length > 0 &&
            !!this.questionDefinition &&
            this.questionDefinition.length > 0 &&
            !!this.llmResultDefinitions &&
            this.llmResultDefinitions.length > 0 &&
            this.llmResultDefinitions.every(
                (definition) =>
                    definition.criteria && definition.criteria.length > 0,
            )
        );
    }

    getConfiguration(): LLMClassifierImplementationConfigurationPromptOnly {
        return {
            ...super.getConfiguration(),
            promptTemplate: this.llmPromptTemplate ?? "",
            questionText: this.questionText ?? "",
            questionDescription: this.questionDefinition ?? "",
        };
    }

    willTemplateChange(): boolean {
        if (this.initialValues.questionText !== this.questionText) return true;
        if (this.initialValues.questionDefinition !== this.questionDefinition)
            return true;

        if (
            !this.initialValues.llmResultDefinitions &&
            !this.llmResultDefinitions
        )
            return false;
        if (
            (!this.initialValues.llmResultDefinitions &&
                this.llmResultDefinitions) ||
            (this.initialValues.llmResultDefinitions &&
                !this.llmResultDefinitions)
        )
            return true;

        for (const definition of this.initialValues.llmResultDefinitions!) {
            const associatedDefinition = this.llmResultDefinitions?.find(
                (def) => def.result === definition.result,
            );
            // a def was removed/missing
            if (!associatedDefinition) return true;

            // criteria didn't match
            if (definition.criteria !== associatedDefinition.criteria)
                return true;

            // # examples mismatch, easy early return
            if (
                definition.examples.length !==
                associatedDefinition.examples.length
            )
                return true;

            for (const example of definition.examples) {
                const associatedExample = associatedDefinition.examples.find(
                    (ex) => ex === example,
                );
                if (associatedExample === undefined) return true;
            }
        }

        return false;
    }
}
